// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, TextInput } from 'grommet'
import { useTranslation } from 'react-i18next'

import {
  ModalDialog,
  ModalHeader,
  Typography,
  Anchor
} from '../../../components'

const FilterSubscription = ({
  onClose,
  selectedEvents,
  inputValues,
  setInputValues
}) => {
  const { t } = useTranslation()
  const [tempInputValues, setTempInputValues] = useState({})

  useEffect(() => {
    setTempInputValues(inputValues)
  }, [inputValues])

  const handleInputChange = (event, value) => {
    if (value) {
      setTempInputValues({
        ...tempInputValues,
        [event.value]: value
      })
    } else {
      const { [event.value]: removedValue, ...rest } = tempInputValues
      setTempInputValues(rest)
    }
  }

  const handleApplyFilter = () => {
    setInputValues(tempInputValues)
    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  const filterExamples = [
    t('automations:webhooks.filter_example_1'),
    t('automations:webhooks.filter_example_2'),
    t('automations:webhooks.filter_example_3'),
    t('automations:webhooks.filter_example_4'),
    t('automations:webhooks.filter_example_5'),
    t('automations:webhooks.filter_example_6')
  ]

  return (
    <ModalDialog
      header={
        <ModalHeader
          title={
            <Typography type="heading" level="2" testId="specify-filters-title">
              {t('automations:webhooks.specify_filters')}
            </Typography>
          }
        />
      }
      content={
        <Box>
          <Typography type="text">
            {t('automations:webhooks.subs_filter_desc')}{' '}
            <Anchor
              href="https://developer.greenlake.hpe.com/docs/greenlake/standards/draft/event/event_format/"
              target="_blank"
            >
              {t('automations:webhooks.greenlake_events')}
            </Anchor>{' '}
            {t('automations:webhooks.subs_filter_desc_2')}
          </Typography>
          <Box pad={{ top: 'medium', bottom: 'medium' }}>
            <Typography type="text">
              {t('automations:webhooks.examples_of_filters')}
            </Typography>
            <ul>
              {filterExamples.map((example) => (
                <li key={example}>
                  <Typography type="text">{example}</Typography>
                </li>
              ))}
            </ul>
          </Box>
          <Box>
            {selectedEvents.map((event) => (
              <Box key={event.value} margin={{ bottom: 'medium' }}>
                <Typography type="text" size="small" weight="500">
                  {event.subTitle}
                </Typography>
                <Typography type="text" size="small" weight="400">
                  {t('automations:webhooks.specify_filter_or_blank', {
                    title: event.title
                  })}
                </Typography>
                <TextInput
                  size="small"
                  value={tempInputValues[event.value] || ''}
                  onChange={(e) => handleInputChange(event, e.target.value)}
                  data-testid="filter-input"
                />
              </Box>
            ))}
          </Box>
          <Box
            margin={{ top: 'medium' }}
            direction="row"
            justify="end"
            gap="small"
          >
            <Button
              label={t('common:cancel')}
              onClick={handleCancel}
              data-testid="cancel-filters-button"
            />
            <Button
              primary
              label={t('common:apply_filters')}
              onClick={handleApplyFilter}
              data-testid="apply-filters-button"
            />
          </Box>
        </Box>
      }
      onClose={handleCancel}
      position="center"
      testId="specify-filters-modal"
      width="large"
    />
  )
}

FilterSubscription.propTypes = {
  onClose: PropTypes.func.isRequired,
  selectedEvents: PropTypes.array.isRequired,
  inputValues: PropTypes.object.isRequired,
  setInputValues: PropTypes.func.isRequired
}

export default FilterSubscription
